export type AutomationChannel = 'Voice call' | 'iMessage' | 'RCS' | 'SMS' | 'Wait' | 'Exit';
export type PlaybookStatus = 'Active' | 'Draft';

export interface AutomationStep {
  id: string;
  day: number;
  order: number;
  channel: AutomationChannel;
  timeWindow: string;
  promptLabel: string;
  script: string;
  conditions: string;
  skipConditions: string;
  fallback: string;
  successBehavior: string;
}

export interface AutomationPlaybook {
  id: string;
  name: string;
  leadType: string;
  description: string;
  status: PlaybookStatus;
  assignedClients: number;
  version: string;
  durationDays: number;
  lastUpdated: string;
  versionNote: string;
  steps: AutomationStep[];
}

export type AssignmentScope = 'Global lead type' | 'Client default' | 'Client + lead type';

export interface AutomationAssignment {
  id: string;
  scope: AssignmentScope;
  clientId: string;
  source: string;
  campaign: string;
  leadType: string;
  playbookId: string;
  patches: string[];
}

export interface AutomationVersion {
  id: string;
  playbookId: string;
  version: string;
  state: 'Draft' | 'Published';
  current: boolean;
  note: string;
  author: string;
  createdAt: string;
  assignedLeads: number;
}

export type SimulatorLineType = 'iPhone' | 'RCS Android' | 'SMS mobile' | 'Landline';

export interface SimulatorInput {
  clientId: string;
  leadType: string;
  source: string;
  lineType: SimulatorLineType;
  state: string;
  localTime: string;
  qualified: boolean;
  replied: boolean;
  dnc: boolean;
  acceptingTransfers: boolean;
  calendarAvailability: boolean;
}

export interface SimulationTraceItem {
  id: string;
  day: number;
  time: string;
  tone: 'action' | 'decision' | 'success' | 'skip' | 'exit';
  channel: AutomationChannel | 'System' | 'Inbound';
  title: string;
  detail: string;
}

const step = (
  id: string,
  day: number,
  order: number,
  channel: AutomationChannel,
  timeWindow: string,
  promptLabel: string,
  script: string,
  conditions: string,
  skipConditions: string,
  fallback: string,
  successBehavior: string,
): AutomationStep => ({
  id,
  day,
  order,
  channel,
  timeWindow,
  promptLabel,
  script,
  conditions,
  skipConditions,
  fallback,
  successBehavior,
});

const freshMortgageSteps: AutomationStep[] = [
  step('fm-00', 0, 0, 'Wait', 'Immediately', 'Consent and line check', 'Validate consent evidence, lead-local time, DNC state, and line capability.', 'Fresh paid-social or web-funnel lead', 'Missing consent, DNC, invalid number, or outside approved hours', 'Queue for next approved local window', 'Continue to the first call'),
  step('fm-01', 0, 1, 'Voice call', '9:00 AM-8:00 PM local', 'Immediate mortgage request call', 'Confirm the submitted request, qualify homeowner status, and offer a warm transfer.', 'Consent verified and callable now', 'Replied, booked, transferred, STOP, DNC, or invalid line', 'Offer a real calendar slot when transfer is unavailable', 'Transfer when qualified and an agent is accepting'),
  step('fm-02', 0, 2, 'iMessage', 'After unanswered call', 'Fresh request follow-up', 'Reference the request, identify the agency, and ask whether now or later is easier.', 'Mobile line and first call unanswered', 'Any reply, landline, STOP, DNC, or active conversation', 'RCS, then compliant SMS', 'Pause blind cadence on reply'),
  step('fm-03', 0, 3, 'Wait', '3 hours', 'Day 0 cooldown', 'Hold every outbound route until the locked Day 0 cooldown clears.', 'No answer and no inbound response', 'Any terminal or engaged state', 'Resume next approved local window', 'Release second-call eligibility'),
  step('fm-04', 0, 4, 'Voice call', 'At least 3 hours later', 'Second Day 0 attempt', 'Reconfirm the request and keep the opening concise.', 'High-intent lead, no reply, and time remains', 'Day 0 call cap reached or less than 15 minutes remain', 'Move to Day 1', 'Transfer or book on qualification'),
  step('fm-05', 1, 0, 'Voice call', 'Opposite Day 0 daypart', 'Alternate-daypart call', 'Call in the opposite daypart and leave the campaign-only voicemail when appropriate.', 'No engagement or terminal outcome', 'Replied, booked, transferred, STOP, or DNC', 'No additional voicemail', 'Transfer or book on qualification'),
  step('fm-06', 2, 0, 'RCS', '10:30 AM-6:30 PM local', 'Choose a contact window', 'Ask whether morning, afternoon, or a specific calendar time is easiest.', 'Messaging-capable line and no reply', 'Landline, STOP, DNC, or active conversation', 'SMS', 'Pause blind cadence on reply'),
  step('fm-07', 3, 0, 'Voice call', '11:00 AM-7:30 PM local', 'Day 3 qualification call', 'Resume the request from context without repeating completed questions.', 'No engagement or terminal outcome', 'Replied, booked, transferred, STOP, or DNC', 'Continue to Day 5', 'Transfer or book on qualification'),
  step('fm-08', 5, 0, 'Voice call', '9:30 AM-6:30 PM local', 'Day 5 outcome call', 'Focus on the easiest next step: connect now or choose a calendar time.', 'No engagement or terminal outcome', 'Replied, booked, transferred, STOP, or DNC', 'Offer two real calendar windows by message', 'Transfer or book on qualification'),
  step('fm-09', 5, 1, 'SMS', 'After unanswered call', 'Two available times', 'Offer two real available appointment times and an easy reply path.', 'Mobile line, calendar connected, and call unanswered', 'Landline, no availability, STOP, DNC, or active conversation', 'Wait for Day 7 call', 'Book selected slot and exit'),
  step('fm-10', 7, 0, 'Voice call', '12:00 PM-7:30 PM local', 'Final call attempt', 'Make the final call attempt without urgency or pressure.', 'No engagement or terminal outcome', 'Replied, booked, transferred, STOP, or DNC', 'Continue to Day 10 closeout', 'Transfer or book on qualification'),
  step('fm-11', 10, 0, 'iMessage', '10:00 AM-6:00 PM local', 'Close request cleanly', 'Explain that proactive follow-up is ending and the lead can reply or call whenever ready.', 'Mobile line and no engagement', 'Landline, STOP, DNC, or active conversation', 'Exit without a message', 'Any reply reopens as inbound-engaged'),
  step('fm-12', 10, 1, 'Exit', 'After closeout', 'Expire managed outreach', 'Stop proactive automation and hand off the record with history and suppression state.', 'No active conversation, booking, or transfer', 'Never skip terminal suppression', 'Keep inbound routing available', 'Mark outbound work window complete'),
];

const finalExpenseSteps: AutomationStep[] = freshMortgageSteps.map((item) => ({
  ...item,
  id: item.id.replace('fm-', 'fe-'),
  promptLabel: item.promptLabel.replace(/mortgage/gi, 'final expense'),
  script: item.script.replace(/homeowner status/gi, 'coverage need and decision-maker status').replace(/mortgage/gi, 'final expense'),
}));

const agedRevivalSteps: AutomationStep[] = [
  step('ar-00', 0, 0, 'Voice call', '10:00 AM-7:00 PM local', 'Permission-first revival call', 'Identify the older request and ask permission to continue before qualifying.', 'Consent remains valid under the approved policy', 'DNC, STOP, stale consent, or wrong number', 'Exit safely', 'Continue only after renewed interest'),
  step('ar-01', 0, 1, 'SMS', 'After unanswered call', 'Still interested check', 'Ask whether the prior request is still relevant and provide a clear STOP path.', 'Mobile line and unanswered call', 'Landline, STOP, DNC, or stale consent', 'No message', 'Pause on reply'),
  step('ar-02', 2, 0, 'Voice call', '11:00 AM-6:30 PM local', 'Revival follow-up', 'Continue only from the prior context and avoid repeating the full pitch.', 'No reply or terminal outcome', 'STOP, DNC, booked, transferred, or engaged', 'Continue to Day 5', 'Transfer or book when qualified'),
  step('ar-03', 5, 0, 'RCS', '11:00 AM-6:00 PM local', 'Simple yes or no', 'Offer a yes, later, or no response so the lead controls the next step.', 'Messaging-capable line', 'Landline, STOP, DNC, or engaged', 'SMS', 'Pause on reply'),
  step('ar-04', 7, 0, 'Voice call', '12:00 PM-7:00 PM local', 'Final revival call', 'Make one final permission-first call.', 'No engagement or terminal outcome', 'STOP, DNC, booked, transferred, or engaged', 'Proceed to closeout', 'Transfer or book when qualified'),
  step('ar-05', 10, 0, 'SMS', '10:00 AM-5:30 PM local', 'Revival closeout', 'Close the request without pressure and keep inbound access available.', 'Mobile line and no engagement', 'Landline, STOP, DNC, or engaged', 'Exit without message', 'Reopen only on inbound interest'),
  step('ar-06', 10, 1, 'Exit', 'After closeout', 'Expire revival', 'End proactive outreach and retain the full audit trail.', 'No engagement', 'Never skip suppression', 'Inbound remains available', 'Hand off to client CRM'),
];

const missedAppointmentSteps: AutomationStep[] = [
  step('ma-00', 0, 0, 'iMessage', 'Within 5 minutes', 'Missed appointment check-in', 'Acknowledge the missed time and offer to reschedule without pressure.', 'Appointment was missed and messaging is available', 'STOP, DNC, or already rescheduled', 'RCS, then SMS', 'Book a new slot and exit'),
  step('ma-01', 0, 1, 'Voice call', 'At least 30 minutes later', 'Reschedule call', 'Offer the next two real calendar openings.', 'No message reply and callable now', 'Replied, booked, STOP, or DNC', 'Continue to Day 1', 'Book selected slot'),
  step('ma-02', 1, 0, 'SMS', '10:00 AM-6:00 PM local', 'Two new times', 'Share two real appointment options and invite a preferred time.', 'Mobile line and appointment still open', 'Landline, booked, STOP, or DNC', 'Continue to Day 3', 'Book selected slot'),
  step('ma-03', 3, 0, 'Voice call', '11:00 AM-7:00 PM local', 'Final reschedule call', 'Make a concise final call focused only on rescheduling.', 'Appointment remains unresolved', 'Replied, booked, STOP, or DNC', 'Continue to Day 5', 'Book selected slot'),
  step('ma-04', 5, 0, 'RCS', '11:00 AM-6:00 PM local', 'Close appointment loop', 'Ask whether to close the appointment request or find another time.', 'Messaging-capable line', 'Landline, booked, STOP, or DNC', 'SMS', 'Pause on reply'),
  step('ma-05', 10, 0, 'Exit', '9:00 AM local', 'Expire reschedule flow', 'Close proactive rescheduling and preserve inbound access.', 'No new appointment', 'Never skip suppression', 'Client receives history', 'Return lead to client follow-up'),
];

export const INITIAL_PLAYBOOKS: AutomationPlaybook[] = [
  {
    id: 'fresh-mortgage',
    name: 'Fresh Mortgage Protection',
    leadType: 'Mortgage protection',
    description: 'Immediate qualification, transfer-first routing, and calendar fallback for fresh requests.',
    status: 'Active',
    assignedClients: 4,
    version: '4.2',
    durationDays: 10,
    lastUpdated: 'Today, 9:18 AM',
    versionNote: 'Clarified Day 5 booking fallback and closeout language.',
    steps: freshMortgageSteps,
  },
  {
    id: 'fresh-final-expense',
    name: 'Fresh Final Expense',
    leadType: 'Final expense',
    description: 'Fresh final-expense follow-up with needs qualification and the same locked contact limits.',
    status: 'Active',
    assignedClients: 2,
    version: '3.7',
    durationDays: 10,
    lastUpdated: 'Yesterday, 4:42 PM',
    versionNote: 'Updated the qualification prompt for household decision makers.',
    steps: finalExpenseSteps,
  },
  {
    id: 'aged-revival',
    name: 'Aged Lead Revival',
    leadType: 'Aged lead',
    description: 'A lighter, permission-first sequence for previously submitted leads with valid consent.',
    status: 'Draft',
    assignedClients: 1,
    version: '2.1-draft',
    durationDays: 10,
    lastUpdated: 'Jul 7, 2:16 PM',
    versionNote: 'Reduced call pressure and added a simple yes-or-no branch.',
    steps: agedRevivalSteps,
  },
  {
    id: 'missed-appointment',
    name: 'Missed Appointment',
    leadType: 'Missed appointment',
    description: 'Event-driven rescheduling that offers real calendar inventory and exits on a new booking.',
    status: 'Active',
    assignedClients: 5,
    version: '1.9',
    durationDays: 10,
    lastUpdated: 'Jul 5, 11:06 AM',
    versionNote: 'Added a lower-pressure Day 5 close-the-loop message.',
    steps: missedAppointmentSteps,
  },
];

export const INITIAL_ASSIGNMENTS: AutomationAssignment[] = [
  { id: 'as-1', scope: 'Global lead type', clientId: '', source: 'Any source', campaign: 'Any campaign', leadType: 'Mortgage protection', playbookId: 'fresh-mortgage', patches: [] },
  { id: 'as-2', scope: 'Global lead type', clientId: '', source: 'Any source', campaign: 'Any campaign', leadType: 'Final expense', playbookId: 'fresh-final-expense', patches: [] },
  { id: 'as-3', scope: 'Client default', clientId: 'northstar', source: 'Any source', campaign: 'Any campaign', leadType: 'Any lead type', playbookId: 'fresh-mortgage', patches: [] },
  { id: 'as-4', scope: 'Client + lead type', clientId: 'keystone', source: 'Web funnel', campaign: 'Homeowner intent - Southeast', leadType: 'Mortgage protection', playbookId: 'fresh-mortgage', patches: ['Day 5: booking-first after 5 PM', 'Transfer window: Priya schedule'] },
  { id: 'as-5', scope: 'Client + lead type', clientId: 'shield', source: 'Paid social lead form', campaign: 'Final Expense - Missouri', leadType: 'Final expense', playbookId: 'fresh-final-expense', patches: ['Day 2: SMS preferred', 'Prompt: household decision maker'] },
  { id: 'as-6', scope: 'Client + lead type', clientId: 'ruiz', source: 'Web funnel', campaign: 'Re-engagement pool', leadType: 'Aged lead', playbookId: 'aged-revival', patches: ['Calls limited to 4', 'No Sunday attempts'] },
];

export const INITIAL_VERSIONS: AutomationVersion[] = [
  { id: 'ver-fm-43', playbookId: 'fresh-mortgage', version: '4.3-draft', state: 'Draft', current: false, note: 'Experiment: move Day 2 message to early afternoon.', author: 'ACL Operations', createdAt: 'Today, 10:04 AM', assignedLeads: 0 },
  { id: 'ver-fm-42', playbookId: 'fresh-mortgage', version: '4.2', state: 'Published', current: true, note: 'Clarified Day 5 booking fallback and closeout language.', author: 'ACL Operations', createdAt: 'Today, 9:18 AM', assignedLeads: 342 },
  { id: 'ver-fm-41', playbookId: 'fresh-mortgage', version: '4.1', state: 'Published', current: false, note: 'Added lead-local time gate before the first call.', author: 'M. Jordan', createdAt: 'Jul 3, 3:46 PM', assignedLeads: 71 },
  { id: 'ver-fm-40', playbookId: 'fresh-mortgage', version: '4.0', state: 'Published', current: false, note: 'Initial transfer-first 10-day production candidate.', author: 'ACL Operations', createdAt: 'Jun 28, 12:22 PM', assignedLeads: 0 },
  { id: 'ver-fe-37', playbookId: 'fresh-final-expense', version: '3.7', state: 'Published', current: true, note: 'Updated household decision-maker qualification.', author: 'ACL Operations', createdAt: 'Yesterday, 4:42 PM', assignedLeads: 188 },
  { id: 'ver-fe-36', playbookId: 'fresh-final-expense', version: '3.6', state: 'Published', current: false, note: 'Adjusted Day 1 alternate daypart.', author: 'M. Jordan', createdAt: 'Jul 1, 10:30 AM', assignedLeads: 29 },
  { id: 'ver-ar-21', playbookId: 'aged-revival', version: '2.1-draft', state: 'Draft', current: true, note: 'Reduced call pressure and added permission branch.', author: 'ACL Operations', createdAt: 'Jul 7, 2:16 PM', assignedLeads: 0 },
  { id: 'ver-ma-19', playbookId: 'missed-appointment', version: '1.9', state: 'Published', current: true, note: 'Added lower-pressure Day 5 close-the-loop message.', author: 'ACL Operations', createdAt: 'Jul 5, 11:06 AM', assignedLeads: 64 },
];

export const LEAD_TYPES = ['Mortgage protection', 'Final expense', 'Aged lead', 'Missed appointment'];
export const SOURCES = ['Paid social lead form', 'Web funnel', 'CRM handoff', 'Missed appointment event'];
export const LINE_TYPES: SimulatorLineType[] = ['iPhone', 'RCS Android', 'SMS mobile', 'Landline'];

export function playbookCounts(playbook: AutomationPlaybook): { calls: number; texts: number } {
  return playbook.steps.reduce((counts, item) => {
    if (item.channel === 'Voice call') counts.calls += 1;
    if (['iMessage', 'RCS', 'SMS'].includes(item.channel)) counts.texts += 1;
    return counts;
  }, { calls: 0, texts: 0 });
}

function messageChannel(lineType: SimulatorLineType): AutomationChannel | null {
  if (lineType === 'iPhone') return 'iMessage';
  if (lineType === 'RCS Android') return 'RCS';
  if (lineType === 'SMS mobile') return 'SMS';
  return null;
}

function isOutsideDefaultWindow(localTime: string): boolean {
  const hour = Number(localTime.split(':')[0]);
  return Number.isFinite(hour) && (hour < 9 || hour >= 20);
}

export function simulateAutomation(input: SimulatorInput): SimulationTraceItem[] {
  const trace: SimulationTraceItem[] = [];
  let index = 0;
  const push = (day: number, time: string, tone: SimulationTraceItem['tone'], channel: SimulationTraceItem['channel'], title: string, detail: string) => {
    index += 1;
    trace.push({ id: `trace-${index}`, day, time, tone, channel, title, detail });
  };

  push(0, input.localTime, 'decision', 'System', 'Lead accepted into policy gate', `${input.source} mapped to ${input.leadType}; ${input.state} local policy and line capability evaluated.`);

  if (input.dnc) {
    push(0, input.localTime, 'exit', 'Exit', 'STOP / DNC exits immediately', 'Voice and every messaging route are suppressed before an outbound attempt. The audit record remains available.');
    return trace;
  }

  const startTime = isOutsideDefaultWindow(input.localTime) ? '09:05' : input.localTime;
  if (isOutsideDefaultWindow(input.localTime)) {
    push(0, input.localTime, 'skip', 'Wait', 'Quiet-hours gate holds outreach', 'The lead stays queued until the next approved recipient-local window.');
  }

  push(0, startTime, 'action', 'Voice call', 'Call 1: immediate qualification', 'Consent and attempt caps pass. The call confirms the request and evaluates qualification.');

  if (input.qualified && input.acceptingTransfers) {
    push(0, startTime, 'success', 'Voice call', 'Qualified lead transfers live', 'An accepting agent is available, so the call bridges immediately and the blind cadence exits.');
    push(0, startTime, 'exit', 'Exit', 'Sequence complete', 'Transfer context and the complete interaction history are retained for the client.');
    return trace;
  }

  if (input.qualified && input.calendarAvailability) {
    push(0, startTime, 'decision', 'System', 'Transfer unavailable; booking path opens', 'No accepting transfer destination is available, so two real calendar times are offered.');
    push(0, startTime, 'success', 'System', 'Appointment booked', 'The selected calendar slot is reserved and the blind cadence exits.');
    push(0, startTime, 'exit', 'Exit', 'Sequence complete', 'Booking context and the prior interaction history are retained for the client.');
    return trace;
  }

  if (input.qualified) {
    push(0, startTime, 'decision', 'System', 'No immediate destination available', 'The lead is qualified, but transfer and calendar capacity are unavailable. The cadence remains eligible for the next approved action.');
  }

  const channel = messageChannel(input.lineType);
  if (channel) {
    push(0, startTime, 'action', channel, `${channel} follows the unanswered call`, 'The message identifies the agency, references the submitted request, and offers an easy reply path.');
  } else {
    push(0, startTime, 'skip', 'System', 'Messaging step skipped for landline', 'No iMessage, RCS, or SMS attempt is created. Voice eligibility remains independent.');
  }

  if (input.replied) {
    push(0, startTime, 'success', 'Inbound', 'Inbound reply pauses blind cadence', 'Scheduled calls and messages stop. The conversation becomes event-driven until resolved or explicitly resumed.');
    push(0, startTime, 'exit', 'Wait', 'Waiting on the active conversation', 'No additional blind outreach occurs while the lead is engaged.');
    return trace;
  }

  push(0, '3 hours later', 'decision', 'Wait', 'Locked cooldown clears', 'The second Day 0 call is eligible only after three hours and only when time remains in the approved window.');
  push(0, 'Later daypart', 'action', 'Voice call', 'Call 2: Day 0 final attempt', 'The second call uses the existing request context and does not repeat completed questions.');
  push(1, 'Opposite daypart', 'action', 'Voice call', 'Call 3: alternate daypart', 'One campaign voicemail may be left; no additional voicemail is scheduled.');

  if (channel) {
    push(2, '1:15 PM', 'action', channel, 'Choose a contact window', 'The lead can choose morning, afternoon, or a specific appointment time.');
  } else {
    push(2, '1:15 PM', 'skip', 'System', 'Day 2 message skipped', 'Landline capability prevents messaging; the action does not consume a messaging attempt.');
  }

  push(3, '5:20 PM', 'action', 'Voice call', 'Call 4: resume qualification', 'The agent resumes from stored context and exits immediately on any terminal state.');
  push(5, '11:40 AM', 'action', 'Voice call', 'Call 5: outcome-focused attempt', 'The call offers either a live connection or a real appointment time.');

  if (channel) {
    push(5, 'After call', 'action', channel, 'Two real appointment times', input.calendarAvailability ? 'Available calendar times are offered directly.' : 'No slot is offered because calendar availability is empty; the message asks for a preferred window instead.');
  } else {
    push(5, 'After call', 'skip', 'System', 'Calendar message skipped', 'The landline remains voice-only.');
  }

  push(7, '6:10 PM', 'action', 'Voice call', 'Call 6: final call attempt', 'The lifetime call cap is now reached; no further outbound call can be created.');

  if (channel) {
    push(10, '11:00 AM', 'action', channel, 'Close request cleanly', 'The final message explains that proactive follow-up is ending and inbound access remains open.');
  } else {
    push(10, '11:00 AM', 'skip', 'System', 'Closeout message skipped', 'The lead is a landline, so the work window closes without a messaging attempt.');
  }

  push(10, '11:01 AM', 'exit', 'Exit', '10-day work window complete', 'Proactive automation stops. The client receives the history, outcome state, and suppression flags; inbound routing stays available.');
  return trace;
}
